/*
  COREEX product-page blocks — reels slider + "pairs well with" slider.

  Both sliders are Swiper, loaded from the same CDN the home-page sections use.
  Focal bundles Flickity, not Swiper, so window.Swiper is always absent and the
  loader below runs; the two libraries share no globals or class names.

  SIZING. Both use slidesPerView: 'auto' and let CSS decide the slide width:

      width: calc((100% - peek - (slides - 1) * gap) / slides)

  That is why "peek" can be an exact px setting. The obvious alternative —
  a fractional slidesPerView like 3.2 — makes peek a ratio of the slide width,
  so it drifts every time the column width or the slide count changes. Gap is
  passed to Swiper as spaceBetween rather than a CSS margin, because in 'auto'
  mode Swiper positions slides from offsetWidth + spaceBetween and would not
  see a margin.

  slidesPerGroupAuto advances a whole page of slides, so the arrows never leave
  the track parked mid-slide.

  REELS PLAYBACK. Three rules, in priority order:

    1. Only one video ever has sound. Unmuting one mutes the rest.
    2. A manual play pauses every other video and re-shows its play button.
    3. Autoplay (optional) plays in-view videos muted, and only once they
       scroll into view — an IntersectionObserver does not fire for something
       already off-screen at load, which is exactly the asked-for behaviour.

  Rules 2 and 3 would fight each other: a manual play pauses its neighbours,
  the observer immediately restarts them. So the first manual interaction sets
  an "engaged" flag on the container and the observer stops STARTING videos
  from then on. It keeps pausing off-screen ones, which is what saves battery.
*/
(function () {
  'use strict';

  /* Both blocks request this file, so with a reels AND a pairs block on the
     page the browser serves one download but runs the script twice. The
     per-element dataset guard below already stops double-init; this stops the
     document-level listeners being registered twice as well. */
  if (window.__coreexPdpBlocksLoaded) return;
  window.__coreexPdpBlocksLoaded = true;

  var CDN_JS = 'https://cdn.jsdelivr.net/npm/swiper@11/swiper-bundle.min.js';
  var CDN_CSS = 'https://cdn.jsdelivr.net/npm/swiper@11/swiper-bundle.min.css';

  function ensureSwiper(cb) {
    if (window.Swiper) return cb();

    if (!document.getElementById('swiper-cdn-css')) {
      var link = document.createElement('link');
      link.id = 'swiper-cdn-css';
      link.rel = 'stylesheet';
      link.href = CDN_CSS;
      document.head.appendChild(link);
    }

    var existing = document.getElementById('swiper-cdn-js');
    if (existing) {
      existing.addEventListener('load', cb);
      return;
    }

    var s = document.createElement('script');
    s.id = 'swiper-cdn-js';
    s.src = CDN_JS;
    s.addEventListener('load', cb);
    document.head.appendChild(s);
  }

  function num(el, attr, fallback) {
    var v = parseFloat(el.getAttribute(attr));
    return isNaN(v) ? fallback : v;
  }


  /* ------------------------------------------------------------------ */
  /* Reels                                                              */
  /* ------------------------------------------------------------------ */

  function initReels(root) {
    if (root.dataset.coreexReady === 'true') return;
    root.dataset.coreexReady = 'true';

    var slider = root.querySelector('.coreex-reels__slider');
    var items = Array.prototype.slice.call(root.querySelectorAll('.coreex-reels__item'));
    if (!slider || !items.length) return;

    var autoplay = root.getAttribute('data-autoplay') === 'true';
    var engaged = false;

    function videoOf(item) {
      return item.querySelector('video');
    }

    function setPlayingState(item, playing) {
      item.classList.toggle('is-playing', playing);
    }

    function pauseAllExcept(keep) {
      items.forEach(function (item) {
        if (item === keep) return;
        var v = videoOf(item);
        if (!v) return;
        v.pause();
        setPlayingState(item, false);
      });
    }

    function muteAllExcept(keep) {
      items.forEach(function (item) {
        if (item === keep) return;
        var v = videoOf(item);
        if (!v) return;
        v.muted = true;
        item.classList.remove('is-unmuted');
      });
    }

    items.forEach(function (item) {
      var video = videoOf(item);
      if (!video) return;

      /* Autoplay requires muted; browsers reject an unmuted programmatic play
         with no user gesture and the promise rejection is silent otherwise. */
      video.muted = true;
      video.playsInline = true;

      var playButton = item.querySelector('.coreex-reels__play');
      var muteButton = item.querySelector('.coreex-reels__mute');

      function play(withSound) {
        engaged = true;
        pauseAllExcept(item);

        if (withSound) {
          video.muted = false;
          item.classList.add('is-unmuted');
          muteAllExcept(item);
        }

        var p = video.play();
        if (p && p.catch) {
          p.catch(function () {
            /* Autoplay policy refused it — fall back to muted, which is
               always allowed, rather than leaving a dead button. */
            video.muted = true;
            item.classList.remove('is-unmuted');
            video.play().catch(function () {});
          });
        }
      }

      if (playButton) {
        playButton.addEventListener('click', function (event) {
          event.preventDefault();
          if (video.paused) {
            play(true);
          } else {
            engaged = true;
            video.pause();
          }
        });
      }

      if (muteButton) {
        muteButton.addEventListener('click', function (event) {
          event.preventDefault();
          engaged = true;

          if (video.muted) {
            video.muted = false;
            item.classList.add('is-unmuted');
            muteAllExcept(item);
            if (video.paused) video.play().catch(function () {});
          } else {
            video.muted = true;
            item.classList.remove('is-unmuted');
          }
        });
      }

      /* The class follows the media element's own events, not the click, so
         it stays correct when a video ends or is paused by another slide. */
      video.addEventListener('play', function () {
        setPlayingState(item, true);
      });
      video.addEventListener('pause', function () {
        setPlayingState(item, false);
      });
      video.addEventListener('ended', function () {
        setPlayingState(item, false);
      });
    });

    if ('IntersectionObserver' in window) {
      var observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            var item = entry.target;
            var video = videoOf(item);
            if (!video) return;

            if (entry.isIntersecting) {
              if (!autoplay) return;
              if (engaged) return;
              video.muted = true;
              video.play().catch(function () {});
            } else {
              video.pause();
            }
          });
        },
        { threshold: 0.5 }
      );

      items.forEach(function (item) {
        observer.observe(item);
      });
    }

    ensureSwiper(function () {
      new window.Swiper(slider, {
        slidesPerView: 'auto',
        slidesPerGroupAuto: true,
        spaceBetween: num(root, 'data-gap-mobile', 8),
        watchOverflow: true,
        breakpoints: {
          741: { spaceBetween: num(root, 'data-gap', 12) }
        }
      });
    });
  }

  /* ------------------------------------------------------------------ */
  /* Pairs well with                                                    */
  /* ------------------------------------------------------------------ */

  function initPairs(root) {
    if (root.dataset.coreexReady === 'true') return;
    root.dataset.coreexReady = 'true';

    var slider = root.querySelector('.coreex-pairs__slider');
    if (!slider) return;

    ensureSwiper(function () {
      new window.Swiper(slider, {
        slidesPerView: 'auto',
        /* advance a whole page of slides, so the arrows never leave the track
           parked mid-slide */
        slidesPerGroupAuto: true,
        spaceBetween: num(root, 'data-gap-mobile', 10),
        watchOverflow: true,
        navigation: {
          nextEl: root.querySelector('.coreex-pairs__arrow--next'),
          prevEl: root.querySelector('.coreex-pairs__arrow--prev'),
          disabledClass: 'is-disabled'
        },
        breakpoints: {
          741: { spaceBetween: num(root, 'data-gap', 16) }
        }
      });
    });
  }

  function initAll(scope) {
    var target = scope || document;
    Array.prototype.forEach.call(target.querySelectorAll('.coreex-reels'), initReels);
    Array.prototype.forEach.call(target.querySelectorAll('.coreex-pairs'), initPairs);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll();
    });
  } else {
    initAll();
  }

  /* Theme editor: a re-rendered block arrives as fresh DOM with no listeners,
     and the dataset guard above stops the untouched ones initialising twice. */
  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });
  document.addEventListener('shopify:block:select', function (event) {
    initAll(event.target);
  });
})();
